import { useState } from 'react';
import { submitContactForm } from '../utils/submitContactForm';


const INITIAL_FORM = {
  nom: '', email: '', tel: '', formule: '', source: '', message: '', consent: false,
  _gotcha: '', 
};

const INITIAL_ERRORS = {
  nom: '', email: '', tel: '', formule: '', source: '', message: '', consent: '',
};


function validateForm(form) {
  const errors = { ...INITIAL_ERRORS };
  let valid = true;

  if (!form.tel.trim()) {
    errors.tel = 'Ce champ est requis.';
    valid = false;
  } else if (!/^\+?[\d\s\-().]{7,20}$/.test(form.tel.trim())) {
    errors.tel = 'Numéro de téléphone invalide.';
    valid = false;
  }

  if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
    errors.email = 'Adresse e-mail invalide.';
    valid = false;
  }

  if (!form.formule) {
    errors.formule = 'Veuillez sélectionner une formule.';
    valid = false;
  }

  if (!form.source) {
    errors.source = 'Veuillez indiquer comment vous nous avez connu.';
    valid = false;
  }

  if (!form.message.trim()) {
    errors.message = 'Ce champ est requis.';
    valid = false;
  } else if (form.message.trim().length < 20) {
    errors.message = 'Merci de détailler un peu plus (min. 20 caractères).';
    valid = false;
  }

  if (!form.consent) {
    errors.consent = 'Vous devez accepter pour continuer.';
    valid = false;
  }

  return { errors, valid };
}



async function submitForm(data: typeof INITIAL_FORM) {
  // Honeypot anti-bot : silencieux, pas d'appel API
  if (data._gotcha) return;

  await submitContactForm({
    nom_complet: data.nom.trim(),
    email: data.email.trim(),
    tel: data.tel.trim(),
    message: data.message.trim(),
    subject: data.formule
      ? `Contact EasyMarket — ${data.formule}`
      : 'Contact depuis easymarket.sn',
    formule: data.formule,
    // Canal d'acquisition marketing (pas l'hostname)
    source: data.source,
    contact_type: 'mise_en_relation',
  });
}


const IconCheck = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <circle cx="10" cy="10" r="10" fill="#22c55e" />
    <path d="M5.5 10.5L8.5 13.5L14.5 7.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconX = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <circle cx="10" cy="10" r="10" fill="#ef4444" />
    <path d="M6.5 6.5L13.5 13.5M13.5 6.5L6.5 13.5" stroke="white" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const IconAlert = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"
    style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }}>
    <circle cx="6" cy="6" r="6" fill="#d61818" />
    <path d="M6 3.5V6.5" stroke="#FFFF" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="6" cy="8.5" r="0.75" fill="#FFFF" />
  </svg>
);

const IconChevron = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"
    style={{ pointerEvents: 'none' }}>
    <path d="M2.5 4.5L6 8L9.5 4.5" stroke="#888" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconSpinner = () => (
  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
  </svg>
);

const IconSend = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M2 8L14 2L9 14L7.5 9.5L2 8Z" stroke="white" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" fill="none" />
  </svg>
);


export default function ContactSection() {
  const [form, setForm]       = useState(INITIAL_FORM);
  const [errors, setErrors]   = useState(INITIAL_ERRORS);
  const [status, setStatus]   = useState('idle'); 
  const [touched, setTouched] = useState({});

  const handle = e => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    const newForm = { ...form, [name]: newValue };
    setForm(newForm);

    if (touched[name]) {
      const { errors: newErrors } = validateForm(newForm);
      setErrors(prev => ({ ...prev, [name]: newErrors[name] }));
    }
  };

  const handleBlur = e => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const { errors: newErrors } = validateForm(form);
    setErrors(prev => ({ ...prev, [name]: newErrors[name] }));
  };

  const handleSubmit = async () => {
    const allTouched = Object.keys(form).reduce((acc, k) => ({ ...acc, [k]: true }), {});
    setTouched(allTouched);

    const { errors: newErrors, valid } = validateForm(form);
    setErrors(newErrors);
    if (!valid) return;

    setStatus('loading');
    try {
      await submitForm(form);
      setStatus('success');
      setForm(INITIAL_FORM);
      setTouched({});
      setErrors(INITIAL_ERRORS);
    } catch {
      setStatus('error');
    }
  };


  const inputBase = "w-full bg-white border-none rounded-[10px] px-4 py-3 text-[13px] font-medium text-[#1a1a1a] outline-none appearance-none font-[Montserrat] transition-shadow duration-150";
  const inputErr  = "ring-2 ring-[#ff5252]";
  const labelClass = "text-[13px] font-semibold text-white mb-[6px] block";
  const errMsg    = "text-[11px] text-[#ff5252] mt-1 font-medium flex items-center gap-1";

  const isLoading = status === 'loading';


  return (
    <section  id="contact" className="bg-white py-[80px] px-6 font-[Montserrat]">

      <div className="text-center mb-10">
        <h2 className="text-[clamp(24px,5vw,38px)] font-black text-[#1a1a1a] mb-2">
          Contactez-nous
        </h2>
        <p className="text-[15px] text-[#000000] font-medium">
  Notre équipe est là pour vous aider
</p>
      </div>

      {status === 'success' && (
        <div className="max-w-[600px] mx-auto mb-6 bg-green-50 border border-green-200 rounded-[12px] px-6 py-4 flex items-center gap-3" role="alert">
          <IconCheck />
          <p className="text-[14px] font-semibold text-green-700">
            Message envoyé ! Nous vous répondrons dans les plus brefs délais.
          </p>
        </div>
      )}

      {status === 'error' && (
        <div className="max-w-[600px] mx-auto mb-6 bg-red-50 border border-red-200 rounded-[12px] px-6 py-4 flex items-center gap-3" role="alert">
          <IconX />
          <p className="text-[14px] font-semibold text-red-700">
            Une erreur est survenue. Veuillez réessayer ou nous contacter directement.
          </p>
        </div>
      )}

      <div className="max-w-[800px] mx-auto bg-gradient-to-br from-[#105088] to-[#053965] rounded-[20px] p-8 md:p-10">

        <div style={{ display: 'none' }} aria-hidden="true">
          <input
            type="text"
            name="_gotcha"
            value={form._gotcha}
            onChange={handle}
            tabIndex={-1}
            autoComplete="off"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className={labelClass} htmlFor="nom">Nom</label>
            <input
              id="nom"
              name="nom"
              value={form.nom}
              onChange={handle}
              onBlur={handleBlur}
              className={`${inputBase} ${errors.nom ? inputErr : ''}`}
              type="text"
              placeholder="Votre Nom"
              autoComplete="name"
              disabled={isLoading}
            />
            {errors.nom && (
              <p className={errMsg} role="alert" id="nom-err">
                <IconAlert />{errors.nom}
              </p>
            )}
          </div>
          <div>
            <label className={labelClass} htmlFor="email">
              E-mail <span className="font-normal opacity-60">(optionnel)</span>
            </label>
            <input
              id="email"
              name="email"
              value={form.email}
              onChange={handle}
              onBlur={handleBlur}
              className={`${inputBase} ${errors.email ? inputErr : ''}`}
              type="email"
              placeholder="Votre email"
              autoComplete="email"
              aria-describedby={errors.email ? 'email-err' : undefined}
              disabled={isLoading}
            />
            {errors.email && (
              <p className={errMsg} role="alert" id="email-err">
                <IconAlert />{errors.email}
              </p>
            )}
          </div>
        </div>

        <div className="mb-4">
          <label className={labelClass} htmlFor="tel">
            Numéro sur lequel on peut vous joindre *
          </label>
          <input
            id="tel"
            name="tel"
            value={form.tel}
            onChange={handle}
            onBlur={handleBlur}
            className={`${inputBase} ${errors.tel ? inputErr : ''}`}
            type="tel"
            placeholder="+221 77 000 00 00"
            autoComplete="tel"
            aria-required="true"
            aria-describedby={errors.tel ? 'tel-err' : undefined}
            disabled={isLoading}
          />
          {errors.tel && (
            <p className={errMsg} role="alert" id="tel-err">
              <IconAlert />{errors.tel}
            </p>
          )}
        </div>

        <div className="mb-4">
          <label className={labelClass} htmlFor="formule">
            Sur quelle formule souhaitez-vous partir ? *
          </label>
          <div className="relative">
            <select
              id="formule"
              name="formule"
              value={form.formule}
              onChange={handle}
              onBlur={handleBlur}
              disabled={isLoading}
              aria-required="true"
              aria-describedby={errors.formule ? 'formule-err' : undefined}
              className={`${inputBase} cursor-pointer ${!form.formule ? 'text-[#11111]' : 'text-[#1a1a1a]'} ${errors.formule ? inputErr : ''}`}
            >
              <option value="" disabled>Sélectionnez</option>
              <option>Formule unique</option>
              <option>Option Ecommerce</option>
            </select>
            <span className="absolute right-[14px] top-1/2 -translate-y-1/2 pointer-events-none">
              <IconChevron />
            </span>
          </div>
          {errors.formule && (
            <p className={errMsg} role="alert" id="formule-err">
              <IconAlert />{errors.formule}
            </p>
          )}
        </div>

        <div className="mb-4">
          <label className={labelClass} htmlFor="source">
            Comment vous nous avez connu ? *
          </label>
          <div className="relative">
            <select
              id="source"
              name="source"
              value={form.source}
              onChange={handle}
              onBlur={handleBlur}
              disabled={isLoading}
              aria-required="true"
              aria-describedby={errors.source ? 'source-err' : undefined}
              className={`${inputBase} cursor-pointer ${!form.source ? 'text-[#11111]' : 'text-[#1a1a1a]'} ${errors.source ? inputErr : ''}`}
            >
              <option value="" disabled>Sélectionnez</option>
              <option>Réseaux sociaux</option>
              <option>Bouche à oreille</option>
              <option>Google</option>
              <option>Événement</option>
              <option>Autre</option>
            </select>
            <span className="absolute right-[14px] top-1/2 -translate-y-1/2 pointer-events-none">
              <IconChevron />
            </span>
          </div>
          {errors.source && (
            <p className={errMsg} role="alert" id="source-err">
              <IconAlert />{errors.source}
            </p>
          )}
        </div>

        <div className="mb-4">
          <label className={labelClass} htmlFor="message">
            Pourriez-vous nous expliquer brièvement votre business ou vos attentes ? *
          </label>
          <textarea
            id="message"
            name="message"
            value={form.message}
            onChange={handle}
            onBlur={handleBlur}
            disabled={isLoading}
            aria-required="true"
            aria-describedby={errors.message ? 'message-err' : undefined}
            className={`${inputBase} resize-none h-[100px] leading-[1.5] ${errors.message ? inputErr : ''}`}
            placeholder="Décrivez votre activité ou vos attentes..."
          />
          <div className="flex justify-between items-center mt-1">
            {errors.message ? (
              <p className={errMsg} role="alert" id="message-err">
                <IconAlert />{errors.message}
              </p>
            ) : <span />}
            <span className="text-[11px] text-white/40">{form.message.length} car.</span>
          </div>
        </div>

        <div className="mb-6">
          <div className="flex items-start gap-[10px]">
            <input
              type="checkbox"
              name="consent"
              id="consent"
              checked={form.consent}
              onChange={handle}
              onBlur={handleBlur}
              disabled={isLoading}
              aria-required="true"
              aria-describedby={errors.consent ? 'consent-err' : undefined}
              className="w-4 h-4 mt-[2px] shrink-0 cursor-pointer accent-[#053965]"
            />
            <label htmlFor="consent" className="text-[12px] font-medium text-white/90 leading-[1.5] cursor-pointer">
              En remplissant ce formulaire, j'autorise l'équipe EasyMarket à me contacter pour d'éventuelles discussions.
            </label>
          </div>
          {errors.consent && (
            <p className={`${errMsg} mt-1 ml-6`} role="alert" id="consent-err">
              <IconAlert />{errors.consent}
            </p>
          )}
        </div>

        <button
          onClick={handleSubmit}
          disabled={isLoading}
          className={`
            w-full bg-[#FFA800] text-white border-none rounded-[10px] py-[15px]
            text-[14px] font-extrabold tracking-[1.5px] uppercase transition-all duration-200
            flex items-center justify-center gap-2
            ${isLoading
              ? 'opacity-70 cursor-not-allowed'
              : 'hover:bg-[#eb8614] cursor-pointer'}
          `}
        >
          {isLoading ? (
            <>
              <IconSpinner />
              Envoi en cours...
            </>
          ) : (
            <>
              <IconSend />
              Envoyer
            </>
          )}
        </button>

      </div>
    </section>
  );
}